import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Bell, Plus, Trash2, Clock, ChevronRight,
  Utensils, Droplets, Dumbbell, Pill, AlarmClock, X, Check
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const getAuthHeader = () => {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ============================================
// TYPES
// ============================================

interface Reminder {
  id: string;
  title: string;
  description?: string;
  category: string;
  time: string;
  days: string[];
  repeatType: string;
  isEnabled: boolean;
  createdAt: string;
}

type ReminderCategory = 'MEAL' | 'WATER' | 'SUPPLEMENT' | 'WORKOUT' | 'GENERAL';

const CATEGORY_CONFIG: Record<ReminderCategory, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  MEAL: {
    label: 'Mese',
    icon: <Utensils size={18} />,
    color: 'text-orange-600',
    bg: 'bg-orange-100',
  },
  WATER: {
    label: 'Apa',
    icon: <Droplets size={18} />,
    color: 'text-blue-600',
    bg: 'bg-blue-100',
  },
  SUPPLEMENT: {
    label: 'Suplimente',
    icon: <Pill size={18} />,
    color: 'text-purple-600',
    bg: 'bg-purple-100',
  },
  WORKOUT: {
    label: 'Antrenament',
    icon: <Dumbbell size={18} />,
    color: 'text-green-600',
    bg: 'bg-green-100',
  },
  GENERAL: {
    label: 'General',
    icon: <Bell size={18} />,
    color: 'text-gray-600',
    bg: 'bg-gray-100',
  },
};

const DAY_LABELS: Record<string, string> = {
  MON: 'Lu',
  TUE: 'Ma',
  WED: 'Mi',
  THU: 'Jo',
  FRI: 'Vi',
  SAT: 'Sa',
  SUN: 'Du',
};

const ALL_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

// Quick suggestion templates
const QUICK_SUGGESTIONS = [
  { title: 'Mic dejun', category: 'MEAL' as ReminderCategory, time: '08:00' },
  { title: 'Pranz', category: 'MEAL' as ReminderCategory, time: '13:00' },
  { title: 'Cina', category: 'MEAL' as ReminderCategory, time: '19:00' },
  { title: 'Gustare', category: 'MEAL' as ReminderCategory, time: '16:00' },
  { title: 'Apa dimineata', category: 'WATER' as ReminderCategory, time: '07:30' },
  { title: 'Hidratare', category: 'WATER' as ReminderCategory, time: '12:00' },
  { title: 'Vitamina D3', category: 'SUPPLEMENT' as ReminderCategory, time: '09:00' },
  { title: 'Omega 3', category: 'SUPPLEMENT' as ReminderCategory, time: '08:30' },
  { title: 'Magneziu', category: 'SUPPLEMENT' as ReminderCategory, time: '21:00' },
  { title: 'Antrenament', category: 'WORKOUT' as ReminderCategory, time: '07:00' },
  { title: 'Cardio', category: 'WORKOUT' as ReminderCategory, time: '18:00' },
];

// ============================================
// TOGGLE SWITCH
// ============================================
function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
        checked ? 'bg-purple-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ============================================
// TIME PICKER (scroll-style)
// ============================================
function TimePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [hours, minutes] = value.split(':').map(Number);

  const hourOptions = Array.from({ length: 24 }, (_, i) => i);
  const minuteOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const selectClass =
    'w-28 text-center text-2xl font-bold text-gray-900 border-2 border-purple-200 rounded-xl py-3 px-2 focus:outline-none focus:border-purple-500 bg-white appearance-none';

  return (
    <div className="flex items-center justify-center gap-3">
      <div className="flex flex-col items-center">
        <label className="text-xs text-gray-500 mb-1.5">Ora</label>
        <select
          value={hours}
          onChange={e => onChange(`${String(parseInt(e.target.value)).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`)}
          className={selectClass}
        >
          {hourOptions.map(h => (
            <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
          ))}
        </select>
      </div>
      <span className="text-3xl font-bold text-gray-400 mt-5">:</span>
      <div className="flex flex-col items-center">
        <label className="text-xs text-gray-500 mb-1.5">Minut</label>
        <select
          value={minutes}
          onChange={e => onChange(`${String(hours).padStart(2, '0')}:${String(parseInt(e.target.value)).padStart(2, '0')}`)}
          className={selectClass}
        >
          {minuteOptions.map(m => (
            <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ============================================
// ADD REMINDER MODAL
// ============================================
interface AddReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (reminder: Omit<Reminder, 'id' | 'createdAt'>) => Promise<void>;
  editReminder?: Reminder | null;
}

function AddReminderModal({ isOpen, onClose, onSave, editReminder }: AddReminderModalProps) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<ReminderCategory>('GENERAL');
  const [time, setTime] = useState('08:00');
  const [days, setDays] = useState<string[]>(ALL_DAYS);
  const [repeatType, setRepeatType] = useState<'DAILY' | 'WEEKLY' | 'ONCE'>('DAILY');
  const [saving, setSaving] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (editReminder) {
      setTitle(editReminder.title);
      setCategory(editReminder.category as ReminderCategory);
      setTime(editReminder.time);
      setDays(editReminder.days);
      setRepeatType(editReminder.repeatType as 'DAILY' | 'WEEKLY' | 'ONCE');
      setCharCount(editReminder.title.length);
    } else {
      setTitle('');
      setCategory('GENERAL');
      setTime('08:00');
      setDays(ALL_DAYS);
      setRepeatType('DAILY');
      setCharCount(0);
    }
  }, [editReminder, isOpen]);

  const toggleDay = (day: string) => {
    setDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const handleSuggestion = (s: typeof QUICK_SUGGESTIONS[0]) => {
    setTitle(s.title);
    setCategory(s.category);
    setTime(s.time);
    setCharCount(s.title.length);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Introdu un titlu pentru reminder');
      return;
    }
    if (days.length === 0) {
      toast.error('Selecteaza cel putin o zi');
      return;
    }
    setSaving(true);
    try {
      await onSave({ title: title.trim(), category, time, days, repeatType, isEnabled: true });
      onClose();
    } catch {
      // error handled in parent
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">
            {editReminder ? 'Editeaza reminder' : 'Adauga reminder'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <X size={16} className="text-gray-600" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">

          {/* Quick suggestions */}
          {!editReminder && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Sugestii rapide</p>
              <div className="flex flex-wrap gap-2">
                {QUICK_SUGGESTIONS.map(s => (
                  <button
                    key={s.title}
                    onClick={() => handleSuggestion(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      title === s.title
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300 hover:text-purple-700'
                    }`}
                  >
                    {s.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Ce vrei sa-ti amintesc?
            </label>
            <div className="relative">
              <input
                type="text"
                value={title}
                onChange={e => { setTitle(e.target.value); setCharCount(e.target.value.length); }}
                maxLength={100}
                placeholder="ex: Vitamina D3, Mic dejun, Antrenament..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-gray-50"
              />
              <span className="absolute right-3 bottom-3 text-xs text-gray-400">{charCount}/100</span>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Categorie</label>
            <div className="grid grid-cols-5 gap-2">
              {(Object.keys(CATEGORY_CONFIG) as ReminderCategory[]).map(cat => {
                const cfg = CATEGORY_CONFIG[cat];
                const isSelected = category === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setCategory(cat)}
                    className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-100 bg-white hover:border-gray-200'
                    }`}
                  >
                    <span className={isSelected ? 'text-purple-600' : cfg.color}>
                      {cfg.icon}
                    </span>
                    <span className={`text-[10px] font-medium leading-tight text-center ${isSelected ? 'text-purple-700' : 'text-gray-600'}`}>
                      {cfg.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              La ce ora?
            </label>
            <TimePicker value={time} onChange={setTime} />
          </div>

          {/* Repeat type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              De cate ori?
            </label>
            <div className="flex gap-2">
              {[
                { value: 'DAILY', label: 'Zilnic' },
                { value: 'WEEKLY', label: 'Saptamanal' },
                { value: 'ONCE', label: 'O singura data' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setRepeatType(opt.value as typeof repeatType)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                    repeatType === opt.value
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Days */}
          {repeatType !== 'ONCE' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                In ce zile?
              </label>
              <div className="flex gap-1.5 flex-wrap">
                {ALL_DAYS.map(day => (
                  <button
                    key={day}
                    onClick={() => toggleDay(day)}
                    className={`w-10 h-10 rounded-full text-sm font-semibold transition-all ${
                      days.includes(day)
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {DAY_LABELS[day]}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => setDays(ALL_DAYS)}
                  className="text-xs text-purple-600 font-medium hover:underline"
                >
                  Toate zilele
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => setDays(['MON', 'TUE', 'WED', 'THU', 'FRI'])}
                  className="text-xs text-purple-600 font-medium hover:underline"
                >
                  Luni - Vineri
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => setDays(['SAT', 'SUN'])}
                  className="text-xs text-purple-600 font-medium hover:underline"
                >
                  Weekend
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-6 pt-3 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="w-full py-3.5 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Check size={18} />
                {editReminder ? 'Salveaza modificarile' : 'Adauga reminder'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// REMINDER CARD
// ============================================
function ReminderCard({
  reminder,
  onToggle,
  onDelete,
  onEdit,
}: {
  reminder: Reminder;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (r: Reminder) => void;
}) {
  const cat = CATEGORY_CONFIG[reminder.category as ReminderCategory] || CATEGORY_CONFIG.GENERAL;
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleting(true);
    try {
      await onDelete(reminder.id);
    } finally {
      setDeleting(false);
    }
  };

  const daysLabel = () => {
    if (reminder.days.length === 7) return 'In fiecare zi';
    if (JSON.stringify(reminder.days.sort()) === JSON.stringify(['FRI', 'MON', 'THU', 'TUE', 'WED'])) return 'Luni - Vineri';
    if (JSON.stringify(reminder.days.sort()) === JSON.stringify(['SAT', 'SUN'])) return 'Weekend';
    return reminder.days.map(d => DAY_LABELS[d]).join(', ');
  };

  return (
    <div
      className={`bg-white rounded-2xl border transition-all ${
        reminder.isEnabled ? 'border-gray-100 shadow-sm' : 'border-gray-100 opacity-60'
      }`}
    >
      <div className="flex items-center gap-3 px-4 py-3.5">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${cat.bg}`}>
          <span className={cat.color}>{cat.icon}</span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" onClick={() => onEdit(reminder)}>
          <p className="font-semibold text-gray-900 text-sm leading-tight truncate">{reminder.title}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-gray-500 font-medium">{reminder.time}</span>
            <span className="text-gray-300 text-xs">·</span>
            <span className="text-xs text-gray-400">{daysLabel()}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-300 hover:text-red-400 hover:bg-red-50 transition-colors"
          >
            {deleting ? (
              <span className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Trash2 size={14} />
            )}
          </button>
          <Toggle checked={reminder.isEnabled} onChange={() => onToggle(reminder.id)} />
        </div>
      </div>
    </div>
  );
}

// ============================================
// MAIN PAGE
// ============================================
const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editReminder, setEditReminder] = useState<Reminder | null>(null);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [pushLoading, setPushLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [remRes, prefRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/reminders`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/notifications/preferences`, { headers: getAuthHeader() }),
      ]);

      if (remRes.status === 'fulfilled') {
        setReminders(remRes.value.data.reminders || []);
      }
      if (prefRes.status === 'fulfilled') {
        setPushEnabled(prefRes.value.data.pushNotificationsEnabled ?? true);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTogglePush = async (enabled: boolean) => {
    setPushEnabled(enabled);
    setPushLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/notifications/preferences`,
        { enabled },
        { headers: getAuthHeader() }
      );
    } catch {
      setPushEnabled(!enabled);
      toast.error('Eroare la actualizare');
    } finally {
      setPushLoading(false);
    }
  };

  const handleSaveReminder = async (data: Omit<Reminder, 'id' | 'createdAt'>) => {
    try {
      if (editReminder) {
        const res = await axios.put(
          `${API_URL}/api/reminders/${editReminder.id}`,
          data,
          { headers: getAuthHeader() }
        );
        setReminders(prev => prev.map(r => r.id === editReminder.id ? res.data.reminder : r));
        toast.success('Reminder actualizat');
      } else {
        const res = await axios.post(
          `${API_URL}/api/reminders`,
          data,
          { headers: getAuthHeader() }
        );
        setReminders(prev => [...prev, res.data.reminder]);
        toast.success('Reminder adaugat');
      }
      setEditReminder(null);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Eroare la salvare');
      throw err;
    }
  };

  const handleToggleReminder = async (id: string) => {
    try {
      const res = await axios.patch(
        `${API_URL}/api/reminders/${id}/toggle`,
        {},
        { headers: getAuthHeader() }
      );
      setReminders(prev => prev.map(r => r.id === id ? res.data.reminder : r));
    } catch {
      toast.error('Eroare la actualizare');
    }
  };

  const handleDeleteReminder = async (id: string) => {
    try {
      await axios.delete(`${API_URL}/api/reminders/${id}`, { headers: getAuthHeader() });
      setReminders(prev => prev.filter(r => r.id !== id));
      toast.success('Reminder sters');
    } catch {
      toast.error('Eroare la stergere');
      throw new Error('delete failed');
    }
  };

  const handleEdit = (reminder: Reminder) => {
    setEditReminder(reminder);
    setShowModal(true);
  };

  // Group reminders by category
  const grouped = reminders.reduce<Record<string, Reminder[]>>((acc, r) => {
    const cat = r.category || 'GENERAL';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(r);
    return acc;
  }, {});

  const categoryOrder: ReminderCategory[] = ['MEAL', 'WATER', 'SUPPLEMENT', 'WORKOUT', 'GENERAL'];

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Header */}
      <div
        className="bg-white border-b border-gray-100 sticky z-30"
        style={{ top: 'env(safe-area-inset-top)' }}
      >
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center justify-between min-h-14 py-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <ArrowLeft size={18} className="text-gray-600" />
              </button>
              <h1 className="font-bold text-gray-900 text-lg">Notificari</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">

        {/* Push Notifications Card */}
        <div
          className="bg-purple-600 rounded-2xl p-4 flex items-center gap-3 cursor-pointer hover:bg-purple-700 transition-colors"
          onClick={() => !pushLoading && handleTogglePush(!pushEnabled)}
        >
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Bell size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-white text-sm">Notificari Push</p>
            <p className="text-purple-200 text-xs">
              Status:{' '}
              <span className={pushEnabled ? 'text-green-300 font-semibold' : 'text-purple-300'}>
                {pushEnabled ? 'Active' : 'Dezactivate'}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {pushLoading ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Toggle checked={pushEnabled} onChange={handleTogglePush} />
            )}
            <ChevronRight size={18} className="text-white/60" />
          </div>
        </div>

        {/* Reminders section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-gray-900 text-base">Remindere personale</h2>
            <span className="text-xs text-gray-400">{reminders.length} active</span>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4 animate-pulse">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="h-3.5 bg-gray-200 rounded w-1/2 mb-2" />
                      <div className="h-2.5 bg-gray-100 rounded w-1/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : reminders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-10 text-center">
              <div className="w-14 h-14 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlarmClock size={24} className="text-purple-400" />
              </div>
              <p className="font-semibold text-gray-700 text-sm mb-1">Niciun reminder setat</p>
              <p className="text-xs text-gray-400 mb-4 px-6">
                Adauga remindere pentru mese, suplimente, hidratare sau antrenamente
              </p>
              <button
                onClick={() => { setEditReminder(null); setShowModal(true); }}
                className="px-5 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-xl hover:bg-purple-700 transition-colors"
              >
                Adauga primul reminder
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {categoryOrder.map(cat => {
                const items = grouped[cat];
                if (!items || items.length === 0) return null;
                const cfg = CATEGORY_CONFIG[cat];
                return (
                  <div key={cat}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${cfg.bg}`}>
                        <span className={`${cfg.color} scale-75`}>{cfg.icon}</span>
                      </div>
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                        {cfg.label}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {items
                        .sort((a, b) => a.time.localeCompare(b.time))
                        .map(reminder => (
                          <ReminderCard
                            key={reminder.id}
                            reminder={reminder}
                            onToggle={handleToggleReminder}
                            onDelete={handleDeleteReminder}
                            onEdit={handleEdit}
                          />
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info card */}
        <div className="bg-blue-50 rounded-2xl p-4 flex gap-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <Clock size={16} className="text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-blue-900 mb-0.5">Notificari in aplicatie</p>
            <p className="text-xs text-blue-700 leading-relaxed">
              Reminderele sunt salvate si vor fi trimise ca notificari push cand aplicatia este instalata pe telefon.
            </p>
          </div>
        </div>
      </div>

      {/* FAB - Add button */}
      <button
        onClick={() => { setEditReminder(null); setShowModal(true); }}
        className="fixed bottom-24 right-5 w-14 h-14 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 active:scale-95 transition-all flex items-center justify-center z-20"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 80px)' }}
      >
        <Plus size={24} />
      </button>

      {/* Modal */}
      <AddReminderModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditReminder(null); }}
        onSave={handleSaveReminder}
        editReminder={editReminder}
      />
    </div>
  );
};

export default NotificationsPage;
